import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import axios from "axios";
import { API } from "@/App";
import { 
  ArrowLeft, 
  Users, 
  BookOpen, 
  Award,
  Download,
  CheckCircle2,
  XCircle
} from "lucide-react";

const AdminPanel = ({ user, onLogout }) => {
  const navigate = useNavigate();
  const [users, setUsers] = useState([]);
  const [progress, setProgress] = useState([]);
  const [certificates, setCertificates] = useState([]);
  const [modules, setModules] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchAdminData();
  }, []);

  const fetchAdminData = async () => {
    try {
      const [usersRes, progressRes, certsRes, modulesRes] = await Promise.all([
        axios.get(`${API}/admin/users`),
        axios.get(`${API}/admin/progress`),
        axios.get(`${API}/admin/certificates`),
        axios.get(`${API}/modules`)
      ]);
      setUsers(usersRes.data);
      setProgress(progressRes.data);
      setCertificates(certsRes.data);
      setModules(modulesRes.data);
    } catch (error) {
      toast.error("Erreur lors du chargement des données");
    } finally {
      setLoading(false);
    }
  };

  const exportToCSV = () => {
    const headers = ["Nom", "Email", "Module", "Score", "Complété", "Date"];
    const rows = progress.map(p => [
      p.user_name,
      p.user_email,
      modules.find(m => m.id === p.module_id)?.title || "N/A",
      p.best_score.toFixed(0) + "%",
      p.completed ? "Oui" : "Non",
      p.completed_at ? new Date(p.completed_at).toLocaleDateString("fr-FR") : "N/A"
    ]);

    const csv = [headers, ...rows].map(row => row.join(",")).join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.setAttribute("download", `progression-animateurs-${new Date().toISOString().split("T")[0]}.csv`);
    document.body.appendChild(link);
    link.click();
    link.remove();
    toast.success("Export CSV réussi!");
  };

  const animators = users.filter(u => u.role === "animator");
  const completedUsers = [...new Set(progress.filter(p => p.completed).map(p => p.user_id))];
  const certificateCount = certificates.length;

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-orange-500"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-orange-50 via-purple-50 to-cream-50">
      {/* Header */}
      <header className="bg-white border-b border-gray-200 shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <Button
              variant="ghost"
              onClick={() => navigate("/dashboard")}
              data-testid="back-to-dashboard-button"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Retour au tableau de bord
            </Button>
            <h1 className="text-2xl font-bold gradient-text">Panneau d'administration</h1>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Stats Cards */}
        <div className="grid md:grid-cols-4 gap-6 mb-8">
          <Card className="border-0 shadow-lg card-hover" data-testid="total-users-card">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600">Total Animateurs</p>
                  <p className="text-3xl font-bold text-gray-900" data-testid="total-users-count">{animators.length}</p>
                </div>
                <div className="p-3 bg-orange-100 rounded-lg">
                  <Users className="w-6 h-6 text-orange-600" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="border-0 shadow-lg card-hover" data-testid="total-modules-card">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600">Modules Actifs</p>
                  <p className="text-3xl font-bold text-gray-900">{modules.length}</p>
                </div>
                <div className="p-3 bg-purple-100 rounded-lg">
                  <BookOpen className="w-6 h-6 text-purple-600" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="border-0 shadow-lg card-hover" data-testid="active-learners-card">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600">En Formation</p>
                  <p className="text-3xl font-bold text-gray-900">{completedUsers.length}</p>
                </div>
                <div className="p-3 bg-blue-100 rounded-lg">
                  <CheckCircle2 className="w-6 h-6 text-blue-600" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="border-0 shadow-lg card-hover" data-testid="certificates-issued-card">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600">Certificats Délivrés</p>
                  <p className="text-3xl font-bold text-gray-900" data-testid="certificates-count">{certificateCount}</p>
                </div>
                <div className="p-3 bg-green-100 rounded-lg">
                  <Award className="w-6 h-6 text-green-600" />
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Tabs */}
        <Card className="border-0 shadow-lg">
          <Tabs defaultValue="progress" className="w-full">
            <CardHeader>
              <TabsList className="grid w-full grid-cols-3">
                <TabsTrigger value="progress" data-testid="progress-tab">Progression</TabsTrigger>
                <TabsTrigger value="users" data-testid="users-tab">Utilisateurs</TabsTrigger>
                <TabsTrigger value="certificates" data-testid="certificates-tab">Certificats</TabsTrigger>
              </TabsList>
            </CardHeader>
            <CardContent>
              {/* Progress Tab */}
              <TabsContent value="progress">
                <div className="space-y-4">
                  <div className="flex justify-between items-center mb-4">
                    <h3 className="text-lg font-semibold">Progression des animateurs</h3>
                    <Button
                      onClick={exportToCSV}
                      variant="outline"
                      data-testid="export-csv-button"
                    >
                      <Download className="w-4 h-4 mr-2" />
                      Exporter CSV
                    </Button>
                  </div>
                  <div className="overflow-x-auto">
                    <table className="w-full" data-testid="progress-table">
                      <thead className="bg-gray-50">
                        <tr>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Animateur</th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Module</th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Score</th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Statut</th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Date</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-200">
                        {progress.map((p, index) => {
                          const module = modules.find(m => m.id === p.module_id);
                          return (
                            <tr key={index} className="hover:bg-gray-50" data-testid={`progress-row-${index}`}>
                              <td className="px-4 py-3">
                                <div>
                                  <p className="font-medium text-gray-900">{p.user_name}</p>
                                  <p className="text-xs text-gray-500">{p.user_email}</p>
                                </div>
                              </td>
                              <td className="px-4 py-3 text-sm text-gray-700">
                                {module ? `Module ${module.order}: ${module.title}` : "N/A"}
                              </td>
                              <td className="px-4 py-3">
                                <span className={`font-semibold ${
                                  p.best_score >= 80 ? "text-green-600" : "text-orange-600"
                                }`}>
                                  {p.best_score.toFixed(0)}%
                                </span>
                              </td>
                              <td className="px-4 py-3">
                                {p.completed ? (
                                  <Badge className="bg-green-500 text-white">
                                    <CheckCircle2 className="w-3 h-3 mr-1" />
                                    Complété
                                  </Badge>
                                ) : (
                                  <Badge variant="outline" className="border-orange-500 text-orange-600">
                                    <XCircle className="w-3 h-3 mr-1" />
                                    En cours
                                  </Badge>
                                )}
                              </td>
                              <td className="px-4 py-3 text-sm text-gray-500">
                                {p.completed_at ? new Date(p.completed_at).toLocaleDateString("fr-FR") : "N/A"}
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                </div>
              </TabsContent>

              {/* Users Tab */}
              <TabsContent value="users">
                <div className="space-y-4">
                  <h3 className="text-lg font-semibold mb-4">Liste des utilisateurs</h3>
                  <div className="space-y-3">
                    {users.map((u, index) => (
                      <Card key={u.id} className="border shadow-sm" data-testid={`user-card-${index}`}>
                        <CardContent className="py-4">
                          <div className="flex items-center justify-between">
                            <div>
                              <p className="font-medium text-gray-900">{u.full_name}</p>
                              <p className="text-sm text-gray-500">{u.email}</p>
                            </div>
                            <Badge variant={u.role === "admin" ? "default" : "outline"}>
                              {u.role === "admin" ? "Administrateur" : "Animateur"}
                            </Badge>
                          </div>
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                </div>
              </TabsContent>

              {/* Certificates Tab */}
              <TabsContent value="certificates">
                <div className="space-y-4">
                  <h3 className="text-lg font-semibold mb-4">Certificats délivrés</h3>
                  {certificates.length === 0 ? (
                    <p className="text-center text-gray-500 py-8">Aucun certificat délivré pour le moment</p>
                  ) : (
                    <div className="space-y-3">
                      {certificates.map((cert, index) => (
                        <Card key={cert.id} className="border shadow-sm" data-testid={`certificate-card-${index}`}>
                          <CardContent className="py-4">
                            <div className="flex items-center justify-between">
                              <div className="flex items-center gap-3">
                                <div className="p-2 bg-green-100 rounded-lg">
                                  <Award className="w-5 h-5 text-green-600" />
                                </div>
                                <div>
                                  <p className="font-medium text-gray-900">{cert.user_name}</p>
                                  <p className="text-sm text-gray-500">N° {cert.certificate_number}</p>
                                </div>
                              </div>
                              <p className="text-sm text-gray-500">
                                {new Date(cert.issued_at).toLocaleDateString("fr-FR")}
                              </p>
                            </div>
                          </CardContent>
                        </Card>
                      ))}
                    </div>
                  )}
                </div>
              </TabsContent>
            </CardContent>
          </Tabs>
        </Card>
      </main>
    </div>
  );
};

export default AdminPanel;
